import { DOCUMENT } from "@angular/common";
import { Injectable, inject } from "@angular/core";
import { Observable, of } from "rxjs";
import { ConnectedReader, FingerprintDevice, FingerprintDeviceError, base64ToBytes } from "./fingerprint-device";

/** Startek FM220U through its local web service (JSONP), e.g. http://localhost:8090/FM220/. */
@Injectable({ providedIn: "root" })
export class StartekDevice implements FingerprintDevice {
  readonly id = "STARTEK";
  readonly label = "Startek FM220";
  private readonly document = inject(DOCUMENT);
  private readonly sdkUrl = process.env.SDKURL_STARTEK;

  // The FM220 service has no status call that doesn't start a capture, so availability is
  // only known when capture runs.
  connect(): Observable<ConnectedReader> {
    return of({ device: this, model: "FM220" });
  }

  capture(): Observable<number[]> {
    return new Observable<number[]>((subscriber) => {
      const callbackName = "_" + new Date().getTime();
      const script = this.document.createElement("script");
      const cleanup = () => {
        script.remove();
        delete (window as any)[callbackName];
      };

      (window as any)[callbackName] = (result: any) => {
        cleanup();
        if (result && result.errorCode == 0) {
          subscriber.next(base64ToBytes(result.wsqBase64));
          subscriber.complete();
        } else if (result && result.errorCode == 103) {
          subscriber.error(new FingerprintDeviceError("capture", "Capture timeout.", -2019));
        } else if (result && result.errorCode !== undefined) {
          // The service answered but the reader failed (not connected, driver missing, ...):
          // a popup asks the user to connect the reader, with the service's own code and text
          const description = result.errorDescription || result.errorMessage || result.status;
          subscriber.error(
            new FingerprintDeviceError(
              "no-device",
              "The Startek FM220 fingerprint reader isn't ready. Connect the FM220 reader to a USB port on " +
                "this PC and check that the FM220 driver is installed, then try again. " +
                `(Error ${result.errorCode}${description ? `: ${description}` : ""})`,
              result.errorCode
            )
          );
        } else {
          subscriber.error(new FingerprintDeviceError("unavailable", this.serviceMessage()));
        }
      };
      script.onerror = () => {
        cleanup();
        subscriber.error(new FingerprintDeviceError("unavailable", this.serviceMessage()));
      };
      script.type = "text/javascript";
      script.src = `${this.sdkUrl}gettmpl?callback=${callbackName}`;
      this.document.body.appendChild(script);

      return cleanup;
    });
  }

  private serviceMessage() {
    return (
      "The Startek FM220 service isn't running on this PC. Install the FM220 driver and service " +
      `and make sure the service is running, then try again. (${this.sdkUrl})`
    );
  }
}
